import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle, Trash2 } from 'lucide-react'
import type { Contact } from '@/types/entities'

interface ContactDeleteDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    contact: Contact | null
    onConfirm: () => void
    isLoading?: boolean
}

export function ContactDeleteDialog({
    open,
    onOpenChange,
    contact,
    onConfirm,
    isLoading = false,
}: ContactDeleteDialogProps) {
    const { t } = useTranslation()
    if (!contact) return null

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Trash2 className="h-5 w-5 text-destructive" />
                        {t('contacts.deleteContact')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('contacts.confirmDelete')} "{`${contact.firstName || ''} ${contact.surname || ''}`.trim() || 'Contact'}"? {t('contacts.deleteWarning')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            {t('contacts.permanentAction')}
                        </AlertDescription>
                    </Alert>

                    <div className="bg-muted p-3 rounded-lg">
                        <div className="text-sm space-y-1">
                            {contact.firstName && (
                                <div><strong>{t('contacts.firstName')}:</strong> {contact.firstName}</div>
                            )}
                            {contact.surname && (
                                <div><strong>{t('contacts.surname')}:</strong> {contact.surname}</div>
                            )}
                            {contact.email && (
                                <div><strong>{t('contacts.email')}:</strong> {contact.email}</div>
                            )}
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={onConfirm}
                        disabled={isLoading}
                    >
                        {isLoading ? t('contacts.deleting') : t('contacts.delete')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
